import { Check, FileText, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import { trpc } from "../../lib/trpc";
import { Avatar, Button } from "../ui";

type Props = {
  clubId: string;
  isAdmin: boolean;
};

export function ApplicationsSection({ clubId, isAdmin: _isAdmin }: Props) {
  const utils = trpc.useUtils();
  const { data: applications, isLoading } = trpc.clubInvite.listPendingApplications.useQuery({
    clubId,
  });

  const reviewMutation = trpc.clubInvite.reviewApplication.useMutation({
    onSuccess: (_, variables) => {
      toast.success(
        variables.status === "approved" ? "Application approved" : "Application rejected",
      );
      void utils.clubInvite.listPendingApplications.invalidate({ clubId });
      void utils.clubMember.list.invalidate({ clubId });
    },
    onError: (err) => toast.error(err.message || "Failed to review application"),
  });

  if (isLoading) {
    return (
      <div className="space-y-3">
        {[1, 2].map((i) => (
          <div key={i} className="h-24 rounded-xl bg-surface-800/50 animate-pulse" />
        ))}
      </div>
    );
  }

  if (!applications || applications.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 rounded-xl bg-surface-800/30 border border-surface-700 text-center">
        <FileText className="size-8 text-surface-500 mb-3" />
        <p className="text-sm text-surface-400">No pending applications</p>
        <p className="text-xs text-surface-500 mt-1">
          Applications from users will appear here for review
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <p className="text-xs font-medium text-surface-400">
        {applications.length} pending application{applications.length !== 1 ? "s" : ""}
      </p>

      {applications.map((app) => (
        <div
          key={app.id}
          className="p-4 rounded-xl bg-surface-800/30 border border-surface-700 space-y-3"
        >
          <div className="flex items-center gap-3">
            <Avatar
              size="sm"
              variant="rounded"
              src={app.avatarUrl}
              initials={app.username.charAt(0)}
              alt={app.username}
            />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-white truncate">{app.username}</p>
              <p className="text-xs text-surface-500">
                Applied {new Date(app.createdAt).toLocaleDateString()}
              </p>
            </div>
          </div>

          {app.answers && app.answers.length > 0 && (
            <div className="p-3 rounded-lg bg-surface-900/50 border border-surface-700">
              <p className="text-xs text-surface-500 font-medium mb-1">Response</p>
              {app.answers.map((answer: string, i: number) => (
                <p key={i} className="text-sm text-surface-300">
                  {answer}
                </p>
              ))}
            </div>
          )}

          <div className="flex items-center gap-2 justify-end">
            <Button
              variant="ghost"
              size="sm"
              className="!text-error-400 hover:!bg-error-500/10"
              disabled={reviewMutation.isPending}
              leftIcon={<X className="size-3.5" />}
              onClick={() =>
                reviewMutation.mutate({
                  applicationId: app.id,
                  clubId,
                  status: "rejected",
                })
              }
            >
              Reject
            </Button>
            <Button
              variant="primary"
              size="sm"
              disabled={reviewMutation.isPending}
              leftIcon={
                reviewMutation.isPending ? (
                  <Loader2 className="size-3.5 animate-spin" />
                ) : (
                  <Check className="size-3.5" />
                )
              }
              onClick={() =>
                reviewMutation.mutate({
                  applicationId: app.id,
                  clubId,
                  status: "approved",
                })
              }
            >
              Approve
            </Button>
          </div>
        </div>
      ))}
    </div>
  );
}
